import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserCircle2, LogOut, ChevronRight, ChevronDown, Settings, MoreHorizontal, X, Lock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { usePermissions } from '../hooks/usePermissions';
import { useSimulation } from '../contexts/SimulationContext';
import { useOrgProfile } from '../hooks/useOrgProfile';
import { DASHBOARD_ITEM, NAV, type NavItem, type NavRequires } from '../config/navConfig';

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface SidebarItemProps {
  icon: React.ComponentType<any>;
  label: string;
  active?: boolean;
  hasSubmenu?: boolean;
  isOpen?: boolean;
  onClick?: () => void;
}

const SidebarItem = ({ icon: Icon, label, active = false, hasSubmenu = false, isOpen = false, onClick }: SidebarItemProps) => (
  <div className="select-none">
    <div
      onClick={onClick}
      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
        active
          ? 'bg-brand/5 dark:bg-blue-500/10 text-brand-dark dark:text-blue-300 border-r-4 border-brand'
          : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} className={active ? 'text-brand' : 'text-slate-400'} />
        <span className={`text-sm font-medium ${active ? 'font-semibold' : ''}`}>{label}</span>
      </div>
      {hasSubmenu && (
        isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />
      )}
    </div>
  </div>
);

interface SubMenuItemProps {
  label: string;
  active?: boolean;
  locked?: boolean;
  onClick?: () => void;
}

const SubMenuItem = ({ label, active, locked, onClick }: SubMenuItemProps) => (
  <div
    onClick={onClick}
    className={`pl-12 pr-4 py-2 text-sm transition-colors flex items-center justify-between ${
      locked
        ? 'text-slate-400 dark:text-slate-600 cursor-default select-none'
        : active
          ? 'text-brand dark:text-blue-400 font-medium bg-brand/5 dark:bg-blue-500/10 cursor-pointer'
          : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 cursor-pointer'
    }`}
  >
    <span>{label}</span>
    {locked && <Lock size={11} className="shrink-0 text-slate-300 dark:text-slate-600" />}
  </div>
);

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { canUseModule, plan, hideUnavailableModules } = usePlan();
  const perms = usePermissions();
  const { isProfile } = useOrgProfile();
  const { isSimulating } = useSimulation();
  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const userRole = user?.role?.name ?? user?.roles?.[0]?.name ?? 'Usuario';

  // ── Gating desde navConfig ──────────────────────────────────────────────────
  const profileOk = (r?: NavRequires): boolean => {
    if (r?.profile && !isProfile(...r.profile)) return false;
    if (r?.profileNot && isProfile(...r.profileNot)) return false;
    return true;
  };
  /** ¿el item se muestra en el menú? (perfil + permiso, excluye hidden) */
  const itemVisible = (it: NavItem): boolean => {
    if (it.hidden) return false;
    if (!profileOk(it.requires)) return false;
    if (it.requires?.can && !it.requires.can(perms)) return false;
    return true;
  };
  /** ¿el item queda bloqueado por no estar incluido en el plan de la organización? */
  const isLocked = (it: NavItem): boolean =>
    it.requires?.planModule ? !canUseModule(it.requires.planModule) : false;

  // "Departamentos" → "Bodegas": relabeling puramente visual cuando el módulo
  // bodegas está activo (el path/permiso/dato de fondo no cambia, ver
  // useDepartmentLabel.ts).
  const isBodega = canUseModule('bodegas');
  const displayLabel = (it: NavItem): string =>
    isBodega && it.path === '/departments' ? 'Bodegas' : it.label;

  const handleNavigate = (path: string) => {
    if (path !== '#') {
      navigate(path);
      if (onMobileClose) onMobileClose();
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSubMenu = (key: string) => {
    setOpenSubMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path: string) => location.pathname === path;
  const isChildActive = (items: NavItem[]) => items.some(child => isActive(child.path));

  const dashboardVisible = itemVisible(DASHBOARD_ITEM);

  const sidebarContent = (
    <div className="h-full flex flex-col">
      {/* Brand */}
      <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
        <img src="/assets/logo_azul.png" alt="ControlNat" className="h-9 w-9 object-contain shrink-0" />
        <div className="flex flex-col leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <span className="text-base font-semibold tracking-tight">
            <span style={{ color: 'var(--brand-dark-blue)' }}>Control</span><span style={{ color: 'var(--brand-bright-blue)' }}>Nat</span>
          </span>
          <span className="text-xs font-medium tracking-wide" style={{ color: 'var(--brand-bright-blue)' }}>Access</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {/* Item suelto: Dashboard */}
        {dashboardVisible && (
          <SidebarItem
            icon={DASHBOARD_ITEM.icon}
            label={DASHBOARD_ITEM.label}
            active={isActive(DASHBOARD_ITEM.path)}
            onClick={() => handleNavigate(DASHBOARD_ITEM.path)}
          />
        )}

        {/* Secciones colapsables */}
        {NAV.map((section) => {
          if (!profileOk(section.requires)) return null;
          const visibleItems = section.items
            .filter(itemVisible)
            // Config de organización (cualquier plan): ocultar en vez de mostrar con candado
            // los items fuera del plan actual.
            .filter((it) => !(isLocked(it) && (hideUnavailableModules || it.requires?.hideWhenLocked)));
          if (visibleItems.length === 0) return null;

          const hasActiveChild = isChildActive(section.items);
          const isOpen = openSubMenus[section.key] || hasActiveChild;

          return (
            <div key={section.key} className="mb-1">
              <SidebarItem
                icon={section.icon}
                label={section.title}
                hasSubmenu
                isOpen={isOpen}
                onClick={() => toggleSubMenu(section.key)}
              />
              {isOpen && (
                <div className="animate-fade-in">
                  {visibleItems.map((child) => {
                    const locked = isLocked(child);
                    return (
                      <SubMenuItem
                        key={child.path}
                        label={displayLabel(child)}
                        active={isActive(child.path)}
                        locked={locked}
                        onClick={() => {
                          if (locked) {
                            window.dispatchEvent(new CustomEvent('plan:module-blocked', {
                              detail: { module: child.requires!.planModule, plan, code: 'MODULE_NOT_AVAILABLE' },
                            }));
                          } else {
                            handleNavigate(child.path);
                          }
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User zone — sticky bottom */}
      <div className="border-t border-slate-100 dark:border-slate-800 relative" ref={userMenuRef}>
        {userMenuOpen && (
          <div className="absolute bottom-full left-3 right-3 mb-2 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700 overflow-hidden z-50 animate-fade-in">
            <button
              onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <UserCircle2 size={16} className="text-slate-400" />
              Mi Perfil
            </button>
            <button
              onClick={() => { setUserMenuOpen(false); navigate('/preferences'); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <Settings size={16} className="text-slate-400" />
              Preferencias
            </button>
            <div className="border-t border-slate-100 mx-3" />
            <button
              onClick={() => { setUserMenuOpen(false); handleLogout(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={16} className="text-red-400" />
              Cerrar Sesión
            </button>
          </div>
        )}

        <button
          onClick={() => setUserMenuOpen(prev => !prev)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
        >
          {user?.avatar_thumbnail ? (
            <img src={user.avatar_thumbnail} alt={user.name || 'Usuario'} className="w-8 h-8 rounded-full object-cover shadow-sm shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-white text-sm font-bold shadow-sm shrink-0">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
          )}
          <div className="overflow-hidden flex-1 text-left">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate leading-tight">{user?.name || 'Usuario'}</p>
            <p className="text-xs text-slate-400 truncate leading-tight">{userRole}</p>
          </div>
          <MoreHorizontal size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={onMobileClose} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-slate-900 shadow-xl z-50 animate-slide-in-from-right"
            style={{ animationDirection: 'reverse', animation: 'slide-in-from-left 0.3s ease-out' }}
          >
            <button
              onClick={onMobileClose}
              className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex-col shrink-0 fixed inset-y-0 left-0 z-10 transition-all duration-300"
        style={{
          top: isSimulating ? 48 : 0,
          height: isSimulating ? 'calc(100% - 48px)' : '100%',
        }}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
